import React from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	verticalChartLine: {
		display: 'block',
		width: '2px',
		minHeight: '20px',
		marginBottom: token('space.100'),
		borderRadius: token('radius.full'),
		backgroundColor: token('color.background.neutral.hovered'),
	},
	avatarItemContainer: {
		maxWidth: '210px',
		width: '100%',
	},
	avatarItemText: {
		textOverflow: 'ellipsis',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
	},
	chartCard: {
		fontWeight: token('font.weight.regular'),
		display: 'flex',
		flexWrap: 'nowrap',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '285px',
		paddingRight: token('space.200'),
		marginBottom: token('space.100'),
		borderRadius: token('radius.medium'),
		backgroundColor: token('color.background.neutral'),
	},
	chartCardSelected: {
		backgroundColor: token('elevation.surface'),
		borderStyle: 'solid',
		borderColor: token('color.border.focused'),
		borderWidth: token('border.width'),
	},
	chartCardClickable: {
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
	reportsList: {
		display: 'grid',
		// Shrink columns to card width — `1fr` + `justifyItems: center` stretched huge gutters between 285px cards.
		gridTemplateColumns: 'repeat(2, max-content)',
		columnGap: token('space.100'),
		justifyContent: 'center',
		width: '100%',
	},
	chartActionsRow: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-end',
		width: '100%',
		marginBottom: token('space.100'),
	},
});

export function VerticalChartLine(): React.JSX.Element {
	return <Box xcss={styles.verticalChartLine} />;
}

export function AvatarItemContainer({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element {
	return <Box xcss={styles.avatarItemContainer}>{children}</Box>;
}

export function AvatarItemText({ children }: { children: React.ReactNode }): React.JSX.Element {
	return <Box xcss={styles.avatarItemText}>{children}</Box>;
}

export function ChartCard({
	children,
	selected = false,
	testId,
	onClick,
}: {
	children: React.ReactNode;
	selected?: boolean;
	testId?: string;
	onClick?: () => void;
}): React.JSX.Element {
	if (onClick) {
		return (
			<Pressable
				xcss={cx(styles.chartCard, selected && styles.chartCardSelected, styles.chartCardClickable)}
				testId={testId}
				onClick={onClick}
			>
				{children}
			</Pressable>
		);
	}

	return (
		<Box xcss={cx(styles.chartCard, selected && styles.chartCardSelected)} testId={testId}>
			{children}
		</Box>
	);
}

export function ReportsList({
	children,
	testId,
}: {
	children: React.ReactNode;
	testId?: string;
}): React.JSX.Element {
	return (
		<Box xcss={styles.reportsList} testId={testId}>
			{children}
		</Box>
	);
}

export function ChartActionsRow({ children }: { children: React.ReactNode }): React.JSX.Element {
	return <Box xcss={styles.chartActionsRow}>{children}</Box>;
}
